import { useEditor, EditorContent } from '@tiptap/react'
import { Editor } from '@tiptap/core'
import { useEffect, useState } from 'react'
import Menu from './Menu'
import extensions from '../config/extensions'
import VariantToolbar from './VariantToolbar'
import { useLanguageTool } from '../hooks/useLanguageTool'
import { SoftcatalaExtension } from '../extensions/softcatala'
import StatusBar from './StatusBar'
import { SuggestionBox } from './suggestions/SuggestionBox'
import { EditorMatch } from '../model/languagetool/types'
import { cleanSuggestion, replaceAlternative, replaceSuggestion } from './suggestions/replacer'
import { OptionSection } from './OptionSection'
import { LinkEditor } from './Link/LinkEditor'
import { LinkContextProvider } from '../hooks/LinkContext'
import { Metrics, getMetrics } from '../metrics'
import { CheckStatus } from "../hooks/CheckStatus"
import SpecialMenu from './SpecialMenu'
import EventEmitter from 'eventemitter3'
import { Events } from '../utils/events'
import { Mode } from '../config/types'
import { ModeSelector } from './ModeSelector'
import { useParaphrase } from '../model/paraphrase/hooks/useParaphrase'
import classNames from 'classnames'
import { ENV } from '../utils/env'

const eventEmitter = window._correctorEvents || (window._correctorEvents = new EventEmitter())

const SoftcatalaEditor = (props: { initialContent: string, content: string }) => {

    const { getMatches, status, setStatus, matchesRepository } = useLanguageTool()
    const { getSentences, sentenceRepository } = useParaphrase()

    const [metrics, setMetrics] = useState<Metrics|null|undefined>(undefined)

    const [currentMatch, setCurrentMatch] = useState<EditorMatch>(new EditorMatch())

    const [ mode, setMode ] = useState<Mode>("SpellCheck" as Mode)

    let triggerSentenceSplit : (() => Promise<void>) | undefined

    const updateMatch = (editor: Editor) => {
        if (mode === "SpellCheck") {
            setCurrentMatch(editor.storage.softcatala.currentMatch)
        } else {
            eventEmitter.emit<Events>('set-text', editor.storage.softcatala.currentMatch)
        }
    }

    const editor = useEditor({
        extensions: [
            SoftcatalaExtension.configure({
                matchesRepository,
                sentenceRepository,
                eventEmitter
            }),
            ...extensions,
        ],
        content: props.content,
        onUpdate({ editor }) {
            setTimeout(() => updateMatch(editor), 5)
            setTimeout(() => !!metrics && setMetrics({...metrics, dirty: true}))
            
        },
        onSelectionUpdate({ editor }) {
            setTimeout(() => updateMatch(editor), 5)
        },
    })

    useEffect(() => {
        if (editor && triggerSentenceSplit) {
            eventEmitter.on<Events>('select-alternative', (e) => {
                replaceAlternative(editor, e)
                setCurrentMatch(new EditorMatch())
                setTimeout(() => triggerSentenceSplit!())
            })    
        }
        return () => {
            eventEmitter.removeAllListeners('select-alternative')
        }
    }, [editor, triggerSentenceSplit])

    if (!editor) {
        return <></>
    }

    if (!triggerSentenceSplit) {
        triggerSentenceSplit = async () => {
            setStatus(CheckStatus.Checking)
            setMode("Paraphrase" as Mode)
            const text = editor.getText()
            editor.setOptions({ editable: false})

            await getSentences(text)
            
            matchesRepository.disable()
            sentenceRepository.enable()

            editor.commands.showSegments(true)
            setStatus(CheckStatus.Idle)
            editor.setOptions({ editable: true})
            editor.commands.focus()
        }
    }

    const triggerSpellCheck = async () => {
        setStatus(CheckStatus.Checking)
        setMode("SpellCheck" as Mode)
        const text = editor.getText()
        editor.setOptions({ editable: false})

        const [matches] = await Promise.all([getMatches(text), getMetrics(text, setMetrics)])
        
        matchesRepository.enable()
        sentenceRepository.disable()

        editor.commands.showSegments(true)
        setStatus(matches === false ? CheckStatus.Error : matches.length ? CheckStatus.Idle : CheckStatus.NoError)
        editor.setOptions({ editable: true})

        editor.commands.focus()
    }

    const replace = (replacement: string) => {
        replaceSuggestion(editor, matchesRepository, currentMatch, replacement)
        setCurrentMatch(new EditorMatch())
    }

    const cleanCurrentErrorInstance = () => {
        cleanSuggestion(editor, matchesRepository, currentMatch)
        setCurrentMatch(new EditorMatch())
    }

    const cleanEditor = () => {
        matchesRepository.reset()
        editor.commands.clearContent(true)
    }

    const sampleText = () => {
        matchesRepository.reset()
        editor.commands.setContent(props.initialContent)
    }

    const getState = () => {
        return editor.state.doc
    }

    const getText = () => {
        return editor.getText()
    }

    const paraphraseDisabled = ENV.PARAPHRASE_DISABLED

    const extraCss = classNames({
        'editor-no-paraphrase': paraphraseDisabled
    })

    return (
        <>
            <ModeSelector mode={mode} triggerParaphrase={triggerSentenceSplit} triggerSpellcheck={triggerSpellCheck} />
            <div className={`contingut-section formulari-eines editor-wrapper ${extraCss}`}>
                <VariantToolbar triggerParaphrase={triggerSentenceSplit} triggerSpellcheck={triggerSpellCheck} mode={mode} />
                <LinkContextProvider editor={editor}>
                    <div className='editor' id="editor-area">
                        <SpecialMenu status={status} getState={getState} getText={getText} clean={cleanEditor} />
                        <Menu editor={editor} />
                        <EditorContent spellCheck={false} editor={editor} />
                        <StatusBar editor={editor} metrics={metrics} status={status} />
                        <SuggestionBox 
                            currentMatch={currentMatch}
                            mode={mode}
                            editor={editor}
                            replace={replace}
                            clean={cleanCurrentErrorInstance}
                        />
                    </div>
                    { editor ? <LinkEditor editor={editor} /> : <></> }
                </LinkContextProvider>
                <OptionSection mode={mode} sampleText={sampleText}/>
            </div>
        </>
    )
}

export default SoftcatalaEditor